import type { GetServerSideProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Product, ProductSummary } from '@/types'

import Image from 'next/image'
import {
    CalendarRange,
    FlaskConical,
    Info,
    MapPin,
    MessageCircle,
    MoveHorizontal,
    Package2,
    ScrollText,
    Sprout,
    Weight
} from 'lucide-react'
import { getProductBySlug, getRelatedProducts } from '@/lib/catalog'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Breadcrumbs } from '@/components/generals/page-hero'
import { CtaBand } from '@/components/generals/cta-band'
import { ButtonLink, buttonClasses } from '@/components/generals/button-link'
import { SectionHeading } from '@/components/generals/section-heading'
import RichText from '@/components/cms/rich-text'
import { ProductCard } from '@/components/products/product-card'
import { ProductEmblem } from '@/components/products/product-emblem'
import { ProductPackage } from '@/components/three/product-package'
import { toPackageLabel } from '@/components/three/package-data'
import { contactInfo } from '@/constants/site'
import { isOptimizableImage } from '@/constants/media'

interface ProductPageProps {
    product: Product
    related: ProductSummary[]
}

type SpecRow = {
    column: 'scientific_name' | 'origin' | 'part_used' | 'harvest_season' | 'packaging' | 'min_order'
    label: string
    icon: LucideIcon
    latin?: boolean
}

const SPEC_ROWS: SpecRow[] = [
    { column: 'scientific_name', label: 'نام علمی', icon: ScrollText, latin: true },
    { column: 'origin', label: 'منشأ برداشت', icon: MapPin },
    { column: 'part_used', label: 'بخش مورد استفاده', icon: Sprout },
    { column: 'harvest_season', label: 'فصل برداشت', icon: CalendarRange },
    { column: 'packaging', label: 'بسته‌بندی', icon: Package2 },
    { column: 'min_order', label: 'حداقل سفارش', icon: Weight }
]

const ProductPage: FC<ProductPageProps> = ({ product, related }): ReactNode => {
    const inquiryHref = { pathname: '/contact', query: { product: product.name } }
    const whatsappText = encodeURIComponent(`سلام، برای «${product.name}» استعلام قیمت دارم.`)
    const whatsappHref = `${contactInfo.whatsapp.url}?text=${whatsappText}`
    const specs = SPEC_ROWS.filter(row => product[row.column])

    return (
        <Layout>
            <Seo
                title={product.name}
                description={product.summary ?? `مشخصات، منشأ برداشت و آزمون‌های آزمایشگاهی ${product.name} از خوزکیمیا زاگرس.`}
                ogImage={product.image ?? undefined}
            />

            <section aria-labelledby='product-title' className='relative overflow-hidden bg-forest-950 text-cream'>
                <div aria-hidden='true' className='bg-girih pointer-events-none absolute inset-0 opacity-[0.06]' />
                <div aria-hidden='true' className='pointer-events-none absolute -top-48 -end-40 size-[36rem] rounded-full bg-gold/10 blur-3xl' />

                <div className='relative mx-auto max-w-7xl px-5 pb-16 pt-10 sm:px-8 lg:pb-24 lg:pt-14'>
                    <Breadcrumbs
                        crumbs={[
                            { label: 'خانه', href: '/' },
                            { label: 'محصولات', href: '/products' },
                            { label: <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span> }
                        ]}
                    />

                    <div className='mt-10 grid gap-14 lg:grid-cols-12 lg:items-center lg:gap-12'>
                        <div className='animate-in fade-in slide-in-from-bottom-3 duration-700 lg:col-span-7'>
                            {product.category_name && product.category_id && (
                                <p className='inline-flex rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-sm font-medium text-gold-light'>
                                    <span data-mdlfy-cms={`categories:${product.category_id}:name`}>
                                        {product.category_name}
                                    </span>
                                </p>
                            )}

                            <h1 id='product-title' className='mt-5 text-[2.6rem] font-bold leading-[1.25] sm:text-5xl lg:text-6xl'>
                                <span data-mdlfy-cms={`products:${product.id}:name`}>
                                    {product.name}
                                </span>
                            </h1>

                            {product.scientific_name && (
                                <p className='mt-3 font-heading text-xl italic text-gold'>
                                    <span dir='ltr' data-mdlfy-cms={`products:${product.id}:scientific_name`}>
                                        {product.scientific_name}
                                    </span>
                                </p>
                            )}

                            {product.summary && (
                                <p
                                    data-mdlfy-cms={`products:${product.id}:summary`}
                                    className='mt-6 max-w-2xl text-base leading-8 text-cream/75 sm:text-lg sm:leading-9'
                                >
                                    {product.summary}
                                </p>
                            )}

                            {(product.origin || product.harvest_season) && (
                                <ul className='mt-8 flex flex-wrap gap-3'>
                                    {product.origin && (
                                        <li className='inline-flex items-center gap-2 rounded-2xl border border-cream/10 bg-cream/5 px-4 py-2.5 text-sm'>
                                            <MapPin aria-hidden='true' className='size-4 text-gold' />
                                            <span className='text-cream/60'>منشأ:</span>
                                            <span data-mdlfy-cms={`products:${product.id}:origin`}>{product.origin}</span>
                                        </li>
                                    )}

                                    {product.harvest_season && (
                                        <li className='inline-flex items-center gap-2 rounded-2xl border border-cream/10 bg-cream/5 px-4 py-2.5 text-sm'>
                                            <CalendarRange aria-hidden='true' className='size-4 text-gold' />
                                            <span className='text-cream/60'>برداشت:</span>
                                            <span data-mdlfy-cms={`products:${product.id}:harvest_season`}>{product.harvest_season}</span>
                                        </li>
                                    )}
                                </ul>
                            )}

                            <div className='mt-10 flex flex-wrap gap-3'>
                                <ButtonLink href={inquiryHref} variant='gold' size='lg'>
                                    درخواست پیش‌فاکتور
                                </ButtonLink>

                                <a
                                    href={whatsappHref}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={buttonClasses('outline-light', 'lg')}
                                >
                                    <MessageCircle aria-hidden='true' />
                                    استعلام در واتس‌اپ
                                </a>
                            </div>
                        </div>

                        <div className='lg:col-span-5'>
                            <div className='relative mx-auto w-full max-w-md'>
                                <div aria-hidden='true' className='absolute -inset-4 rounded-t-full rounded-b-[2.75rem] border border-gold/20' />

                                <div className='relative aspect-[4/5] overflow-hidden rounded-t-full rounded-b-[2rem] border border-gold/40 bg-forest-900 shadow-[0_50px_100px_-40px_rgba(0,0,0,0.9)]'>
                                    {product.image ? (
                                        <Image
                                            src={product.image}
                                            alt={product.image_alt || product.name}
                                            fill
                                            preload
                                            sizes='(min-width: 1024px) 28rem, 92vw'
                                            unoptimized={!isOptimizableImage(product.image)}
                                            className='object-cover'
                                        />
                                    ) : (
                                        <>
                                            <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(201,162,74,0.28),transparent_62%)]' />
                                            <div aria-hidden='true' className='bg-girih absolute inset-0 opacity-[0.06]' />
                                            <ProductPackage
                                                label={toPackageLabel(product)}
                                                fallback={<ProductEmblem categorySlug={product.category_slug} size='large' />}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            {!product.image && (
                                <p className='mt-6 flex items-center justify-center gap-2 text-xs text-cream/50'>
                                    <MoveHorizontal aria-hidden='true' className='size-4 text-gold/70' />
                                    برای دیدن همه طرف‌های بسته، آن را بکشید
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section aria-label='جزئیات محصول' className='py-16 lg:py-24'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12'>
                    <div className='lg:col-span-7 xl:col-span-8'>
                        {product.description && (
                            <div>
                                <h2 className='text-3xl font-bold text-forest-900'>
                                    درباره محصول
                                </h2>

                                <RichText
                                    html={product.description}
                                    className='mt-6 text-base leading-8 text-foreground/85 sm:text-[1.05rem] sm:leading-9 [&_h3]:text-forest-800 [&_a]:text-forest-700'
                                />
                            </div>
                        )}

                        {product.uses.length > 0 && (
                            <div className='mt-12'>
                                <h2 className='text-3xl font-bold text-forest-900'>
                                    کاربردها
                                </h2>

                                <ul className='mt-5 flex flex-wrap gap-2.5'>
                                    {product.uses.map((use, index) => (
                                        <li
                                            key={`${index}-${use}`}
                                            className='rounded-full border border-sand bg-card px-4 py-2 text-sm text-forest-800'
                                        >
                                            {use}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <p className='mt-12 flex items-start gap-3 rounded-2xl border border-sand bg-parchment/60 px-5 py-4 text-sm leading-7 text-muted-foreground'>
                            <Info aria-hidden='true' className='mt-1 size-4 shrink-0 text-gold-deep' />
                            این اطلاعات جنبه آگاهی‌بخشی دارد و جایگزین مشورت با پزشک یا متخصص طب سنتی نیست. در دوران بارداری و شیردهی یا هنگام مصرف دارو، پیش از استفاده با پزشک مشورت کنید.
                        </p>
                    </div>

                    <aside className='space-y-6 lg:col-span-5 xl:col-span-4' aria-label='مشخصات و آزمون‌ها'>
                        {specs.length > 0 && (
                            <div className='rounded-[1.75rem] border border-sand bg-card p-6 lg:p-7'>
                                <h2 className='text-2xl font-bold text-forest-900'>
                                    مشخصات فنی
                                </h2>

                                <dl className='mt-5 divide-y divide-sand'>
                                    {specs.map(({ column, label, icon: Icon, latin }) => (
                                        <div key={column} className='flex items-start gap-3 py-3.5'>
                                            <dt className='flex w-36 shrink-0 items-start gap-2.5 text-sm text-muted-foreground'>
                                                <Icon aria-hidden='true' className='mt-1 size-4 shrink-0 text-gold-deep' />
                                                {label}
                                            </dt>

                                            <dd className={latin ? 'font-heading text-base italic text-forest-900' : 'text-sm font-medium text-forest-900'}>
                                                <span
                                                    dir={latin ? 'ltr' : undefined}
                                                    data-mdlfy-cms={`products:${product.id}:${column}`}
                                                >
                                                    {product[column]}
                                                </span>
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {product.lab_tests.length > 0 && (
                            <div className='rounded-[1.75rem] border border-forest-700/20 bg-forest-900 p-6 text-cream lg:p-7'>
                                <h2 className='flex items-center gap-2 text-2xl font-bold'>
                                    <FlaskConical aria-hidden='true' className='size-5 text-gold' />
                                    آزمون‌های آزمایشگاهی
                                </h2>

                                <p className='mt-2 text-sm leading-6 text-cream/65'>
                                    هر بچ از این محصول پیش از بسته‌بندی این آزمون‌ها را پشت سر می‌گذارد:
                                </p>

                                <ul className='mt-5 space-y-3'>
                                    {product.lab_tests.map((test, index) => (
                                        <li key={`${index}-${test}`} className='flex items-center gap-3 text-sm'>
                                            <span aria-hidden='true' className='size-1.5 shrink-0 rotate-45 bg-gold' />
                                            {test}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className='rounded-[1.75rem] border border-gold/40 bg-gold/10 p-6 lg:p-7'>
                            <p className='font-heading text-xl font-bold text-forest-900'>
                                خرید عمده و صادرات
                            </p>

                            <p className='mt-2 text-sm leading-7 text-muted-foreground'>
                                برای دریافت قیمت، نمونه و برگه آنالیز این محصول، استعلام خود را ارسال کنید.
                            </p>

                            <ButtonLink href={inquiryHref} variant='primary' className='mt-5 w-full'>
                                ارسال استعلام قیمت
                            </ButtonLink>
                        </div>
                    </aside>
                </div>
            </section>

            {related.length > 0 && (
                <section aria-labelledby='related-title' className='border-t border-sand bg-parchment/50 py-16 lg:py-24'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <SectionHeading
                            id='related-title'
                            eyebrow='محصولات مرتبط'
                            title='شاید این‌ها را هم بخواهید'
                        />

                        <ul className='mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map(item => (
                                <li key={item.id}>
                                    <ProductCard product={item} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            <CtaBand />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true }

    const product = await getProductBySlug(slug)

    if (!product) return { notFound: true }

    let related: ProductSummary[] = []

    try {
        related = await getRelatedProducts(product, 3)
    }
    catch (error) {
        console.error('Loading related products failed:', error instanceof Error ? error.message : error)
    }

    return { props: { product, related } }
}

export default ProductPage
